import { useEffect, useState } from "react";
import { api, apiError, currencyFmt, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Building2, Plus, Loader2, LogIn, Users as UsersIcon, Trash2, Search, Image as ImageIcon, Pencil } from "lucide-react";
import { TablePager } from "@/components/TablePager";
import { LogoManager } from "@/components/LogoManager";
import { toast } from "sonner";

export default function Tenants() {
  const { impersonate } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [usersOpen, setUsersOpen] = useState(false);
  const [activeTenant, setActiveTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const [logoTenant, setLogoTenant] = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  const [editName, setEditName] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tenants", form);
      toast.success("Tenant created");
      setOpen(false);
      setForm({ name: "", admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/tenants/${t.tenant_id}`, { active: !t.active });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openUsers = async (t) => {
    setActiveTenant(t);
    setUsersOpen(true);
    setUsersLoading(true);
    setTenantUsers([]);
    try {
      const { data } = await api.get("/users", { params: { tenant_id: t.tenant_id } });
      setTenantUsers(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUsersLoading(false); }
  };

  const loginAs = async (u) => {
    setImpersonating(u.user_id);
    try {
      await impersonate(u.user_id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setImpersonating(null);
    }
  };

  const removeTenant = async (t) => {
    try {
      await api.delete(`/tenants/${t.tenant_id}`);
      toast.success("Tenant deleted");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEdit = (t) => { setEditTenant(t); setEditName(t.name || ""); };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/tenants/${editTenant.tenant_id}`, { name: editName.trim() });
      toast.success("Tenant updated");
      setEditTenant(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? tenants.filter((t) => (t.name || "").toLowerCase().includes(q)) : tenants;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage organizations across the platform.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search tenants…" className="w-56 pl-9 rounded-sm" data-testid="tenant-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="new-tenant-btn"><Plus className="h-4 w-4 mr-2" />New Tenant</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Tenant</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Organization name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" className="rounded-sm" />
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mt-3 mb-2">Tenant Administrator (optional)</p>
                <div className="space-y-3">
                  <Input placeholder="Admin name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="tenant-admin-name" className="rounded-sm" />
                  <Input type="email" placeholder="Admin email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="tenant-admin-email" className="rounded-sm" />
                  <PasswordInput placeholder="Admin password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="tenant-admin-password" className="rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="rounded-sm" data-testid="tenant-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{tenants.length === 0 ? "No tenants yet. Create your first organization." : "No tenants match your search."}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Claims</TableHead>
                <TableHead className="text-right">Approved Spend</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-right">Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((t) => (
                <TableRow key={t.tenant_id} className="hover:bg-muted/50" data-testid={`tenant-row-${t.tenant_id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-right font-mono">{t.users}</TableCell>
                  <TableCell className="text-right font-mono">{t.claims}</TableCell>
                  <TableCell className="text-right font-mono">{currencyFmt(t.approved_amount)}</TableCell>
                  <TableCell className="text-center">
                    <Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`tenant-toggle-${t.tenant_id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openUsers(t)} data-testid={`tenant-users-${t.tenant_id}`}>
                        <UsersIcon className="h-4 w-4 mr-2" />Log in as user
                      </Button>
                      <Button variant="outline" size="icon" className="rounded-sm" onClick={() => openEdit(t)} title="Edit tenant" data-testid={`tenant-edit-${t.tenant_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="rounded-sm" onClick={() => setLogoTenant(t)} title="Manage logo" data-testid={`tenant-logo-${t.tenant_id}`}>
                        <ImageIcon className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`tenant-delete-${t.tenant_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-tenant-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {t.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the organization along with its <span className="font-medium">{t.users} user(s)</span>, <span className="font-medium">{t.claims} claim(s)</span> and all its categories. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`tenant-delete-cancel-${t.tenant_id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => removeTenant(t)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`tenant-delete-confirm-${t.tenant_id}`}>
                              Delete Tenant
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePager page={safePage} pageCount={pageCount} total={filtered.length} shown={paged.length}
          onChange={setPage} testId="tenants-pager" />
      </div>

      <Dialog open={usersOpen} onOpenChange={setUsersOpen}>
        <DialogContent className="max-w-lg" data-testid="tenant-users-dialog">
          <DialogHeader><DialogTitle>{activeTenant?.name} — Log in as a user</DialogTitle></DialogHeader>
          {usersLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading users…</div>
          ) : tenantUsers.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No users in this tenant yet.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
              {tenantUsers.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-3 py-3" data-testid={`tenant-user-${u.user_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="rounded-full text-xs">{ROLE_LABEL[u.role]}</Badge>
                    {!u.active && <span className="text-xs text-muted-foreground">Disabled</span>}
                    <Button size="sm" className="rounded-sm" disabled={!u.active || impersonating === u.user_id}
                      onClick={() => loginAs(u)} data-testid={`login-as-${u.user_id}`}>
                      {impersonating === u.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1.5" />Log in</>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logoTenant} onOpenChange={(o) => !o && setLogoTenant(null)}>
        <DialogContent data-testid="tenant-logo-dialog">
          <DialogHeader><DialogTitle>{logoTenant?.name} — Logo</DialogTitle></DialogHeader>
          {logoTenant && <LogoManager tenantId={logoTenant.tenant_id} testId="tenant-logo-manager" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTenant} onOpenChange={(o) => !o && setEditTenant(null)}>
        <DialogContent data-testid="tenant-edit-dialog">
          <DialogHeader><DialogTitle>Edit tenant</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Organization name</Label>
              <Input required value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-sm" data-testid="tenant-edit-name" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy || !editName.trim()} className="rounded-sm" data-testid="tenant-edit-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
